import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Download, Mail, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { VoucherCard, ukToday, niceDate } from "@/components/VoucherCard";
import { DeliveryFields } from "@/pages/Gift";
import { Notice } from "@/pages/Auth";
import { api, apiError, API } from "@/lib/api";

function Delivery({ token, gift, onChange }) {
  const [form, setForm] = useState({ recipient_email: gift.recipient_email || "", send_on: gift.send_on || ukToday() });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(""); };
  const save = async (clear) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/gifts/view/${token}/schedule`, clear ? {} : form);
      onChange(data); setOpen(false);
    } catch (e) { setError(apiError(e)); }
    setBusy(false);
  };
  const st = gift.recipient_email_status;
  if (gift.state === "redeemed") return <Notice testid="gift-delivery-redeemed">{gift.recipient_name} has already used this gift. Thank you!</Notice>;
  if (st === "sent" || st === "sending") return <Notice testid="gift-delivery-sent">Emailed to {gift.recipient_email}.</Notice>;
  return (
    <div className="rounded-2xl border border-[#33264A] bg-[#1A1222] p-5 space-y-4" data-testid="gift-delivery">
      <p className="flex gap-2 text-[#EDE6F7]"><Mail className="h-5 w-5 mt-0.5 text-[#B57BFF] shrink-0" aria-hidden="true" />
        <span data-testid="gift-delivery-status">{st === "scheduled" ? `We'll email it to ${gift.recipient_email} on ${niceDate(gift.send_on)}.`
          : st === "failed" ? `We couldn't email ${gift.recipient_email}. Please check the address, or print the voucher instead.`
          : "Prefer us to email it to them on a chosen date?"}</span></p>
      {open ? (
        <>
          <DeliveryFields form={form} set={set} idPrefix="delivery" />
          {error && <Notice tone="error" testid="gift-delivery-error">{error}</Notice>}
          <div className="flex flex-wrap gap-3">
            <button type="button" disabled={busy} onClick={() => save(false)} className="gold-btn h-12 px-6 rounded-full disabled:opacity-60" data-testid="gift-delivery-save">Save delivery</button>
            <button type="button" onClick={() => setOpen(false)} className="h-12 px-5 text-[#C9C2D8] underline underline-offset-4" data-testid="gift-delivery-cancel">Cancel</button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap gap-4">
          <button type="button" onClick={() => setOpen(true)} className="text-[#B57BFF] font-semibold underline underline-offset-4" data-testid="gift-delivery-edit">{st === "scheduled" || st === "failed" ? "Change" : "Schedule an email"}</button>
          {st === "scheduled" && <button type="button" disabled={busy} onClick={() => save(true)} className="text-[#C9C2D8] underline underline-offset-4" data-testid="gift-delivery-clear">Don't email it, I'll give it myself</button>}
        </div>
      )}
    </div>
  );
}

export default function GiftSuccess() {
  const [params] = useSearchParams();
  const token = params.get("gift");
  const [gift, setGift] = useState(null);
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!token) { setState("unknown"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/gifts/view/${token}`);
        setGift(data);
        if (["paid", "redeemed", "expired"].includes(data.state)) return setState("paid");
        if (data.state === "expired_checkout") return setState("expired");
        tries.current += 1;
        if (tries.current >= 15) return setState("pending");
        timer = setTimeout(poll, 2000);
      } catch { setState("unknown"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [token]);

  const shell = (children, testid) => (
    <div className="min-h-screen legacy-bg"><Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-6 fade-up" data-testid={testid}>{children}</main></div>
  );
  if (state === "checking") return shell(<div className="glass-card p-8 text-center"><Loader2 className="h-10 w-10 mx-auto text-[#B57BFF] animate-spin mb-4" aria-hidden="true" /><h1 className="font-serif text-3xl text-[#EDE6F7]" role="status">Confirming your payment…</h1><p className="text-lg text-[#B9B3C8] mt-2">This usually takes a few seconds. Please keep this page open.</p></div>, "gift-checking");
  if (state !== "paid") return shell(<div className="glass-card p-8 text-center"><h1 className="font-serif text-3xl text-[#EDE6F7] mb-3" data-testid="gift-status-heading">{state === "pending" ? "Your payment hasn't been confirmed yet" : state === "expired" ? "Your checkout timed out" : "We couldn't find this gift"}</h1><p className="text-lg text-[#B9B3C8] mb-6">{state === "pending" ? "If you completed payment, it can take a moment. We'll email your voucher as soon as it's confirmed." : "You haven't been charged. You can start again whenever you're ready."}</p><Link to="/gift" className="gold-btn inline-flex items-center justify-center h-14 px-8 rounded-full text-lg" data-testid="gift-start-again">Back to gifts</Link></div>, `gift-${state}`);
  return shell(
    <>
      <div className="text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-[#B57BFF] mb-3" aria-hidden="true" />
        <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7]" data-testid="gift-success-heading">Thank you. Your gift for {gift.recipient_name} is ready.</h1>
        <p className="text-lg text-[#B9B3C8] mt-2">We've emailed your receipt and voucher to {gift.buyer_email}. £{(gift.total_pence / 100).toFixed(2)} · One-off purchase. No subscription.</p>
      </div>
      <VoucherCard recipient={gift.recipient_name} buyer={gift.buyer_name} message={gift.message} code={gift.code} validUntil={gift.valid_until} includes={gift.includes} testid="gift-voucher" />
      <a href={`${API}/gifts/view/${token}/voucher.pdf`} target="_blank" rel="noreferrer" className="gold-btn flex items-center justify-center gap-2 w-full h-16 rounded-full text-xl" data-testid="gift-download-pdf">
        <Download className="h-5 w-5" aria-hidden="true" /> Download printable voucher (PDF)
      </a>
      <Delivery token={token} gift={gift} onChange={setGift} />
      <p className="text-sm text-[#8C84A0] text-center">They can unlock it at {window.location.host}/redeem. The code can be used once and is valid until {gift.valid_until}.</p>
    </>, "gift-success");
}
